import { IconDownload, IconExternalLink, IconLogout, IconMailCheck, IconShieldCheck, IconTrash } from "@tabler/icons-react";
import { useEffect, useState, type FormEvent } from "react";
import { api, type Lang, type OwnMessage, type ReplyVia } from "../api";
import { hasServiceConsent } from "../components/ConsentCard";
import { NoticesCard } from "../components/NoticesCard";
import { Card, ErrorNote, Field, SectionTitle, Spinner } from "../components/ui";
import { formatDate, formatDateTime } from "../format";
import { LANG_NAMES, LANGS } from "../i18n";
import { navigate } from "../router";
import { useSession } from "../session";

export function AccountPage() {
  const { me, brand, brandInfo, lang, setLang, setMe, signOut, t } = useSession();
  const [name, setName] = useState(me?.identity.name ?? "");
  const [phone, setPhone] = useState(me?.identity.phone ?? "");
  const [language, setLanguage] = useState<Lang>(lang);
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [messages, setMessages] = useState<OwnMessage[] | undefined>(undefined);
  const [consentBusy, setConsentBusy] = useState<string | null>(null);
  const [viaState, setViaState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [exportState, setExportState] = useState<"idle" | "busy" | "done" | "error">("idle");
  const [deleteState, setDeleteState] = useState<"idle" | "confirm" | "busy" | "sent" | "error">("idle");

  useEffect(() => {
    if (!me) return;
    setName(me.identity.name ?? "");
    setPhone(me.identity.phone ?? "");
  }, [me]);

  // Arriving from the trip page's "enable notifications" nudge: scroll to the notices card.
  useEffect(() => {
    if (window.location.hash !== "#notices") return;
    const id = window.setTimeout(() => document.getElementById("notices")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!me || me.session.kind !== "full") {
      setMessages([]);
      return;
    }
    api
      .messages()
      .then((res) => setMessages(res.messages))
      .catch(() => setMessages([]));
  }, [me]);

  if (!me || !brand) return null;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setState("saving");
    try {
      const updated = await api.patchMe({ name: name.trim(), phone: phone.trim(), language });
      setLang(language);
      setMe(updated);
      setState("saved");
      window.setTimeout(() => setState("idle"), 2000);
    } catch {
      setState("error");
    }
  };

  const brands = brandInfo?.brands ?? [];
  const consentOf = (code: string) => me.consents.some((c) => c.brand === code && c.purpose === "marketing" && c.given);

  const toggleConsent = async (code: string, given: boolean) => {
    setConsentBusy(code);
    try {
      const res = await api.setConsent({ brand: code, purpose: "marketing", given });
      setMe({ ...me, consents: res.consents });
    } finally {
      setConsentBusy(null);
    }
  };

  const logout = async () => {
    await signOut();
    navigate("/signin", { replace: true });
  };

  const isFull = me.session.kind === "full";

  /** docs/16 §6: the export is a file to save; fetch it with the session cookie and hand it to the browser as a download. */
  const exportData = async () => {
    setExportState("busy");
    try {
      const { blob, filename } = await api.exportData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "external";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setExportState("done");
      window.setTimeout(() => setExportState("idle"), 3000);
    } catch {
      setExportState("error");
    }
  };

  const requestDeletion = async () => {
    setDeleteState("busy");
    try {
      await api.requestDeletion();
      setDeleteState("sent");
    } catch {
      setDeleteState("error");
    }
  };
  const storedVia = me.identity.preferences.replyVia;
  const replyVia: ReplyVia = storedVia === "area" || storedVia === "whatsapp" || storedVia === "email" ? storedVia : "email";

  const saveReplyVia = async (next: ReplyVia) => {
    setViaState("saving");
    try {
      setMe(await api.patchMe({ preferences: { replyVia: next } }));
      setViaState("saved");
      window.setTimeout(() => setViaState("idle"), 2000);
    } catch {
      setViaState("error");
    }
  };

  return (
    <main className="page">
      <h1>{t("account")}</h1>
      <p className="muted small">{t("memberSince", { date: formatDate(me.identity.createdAt, lang) })}</p>

      <SectionTitle>{t("yourDetails")}</SectionTitle>
      <form className="card form" onSubmit={(e) => void save(e)}>
        <Field label={t("email")}>
          <input type="email" value={me.identity.email} readOnly />
        </Field>
        <Field label={t("name")}>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" disabled={!isFull} />
        </Field>
        <Field label={t("phone")}>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" placeholder="+351 9…" disabled={!isFull} />
        </Field>
        <Field label={t("language")}>
          <select value={language} onChange={(e) => setLanguage(e.target.value as Lang)}>
            {LANGS.map((l) => (
              <option key={l} value={l}>
                {LANG_NAMES[l]}
              </option>
            ))}
          </select>
        </Field>
        {state === "error" && <ErrorNote>{t("somethingWrong")}</ErrorNote>}
        {isFull ? (
          <button type="submit" className="button button-primary" disabled={state === "saving"}>
            {state === "saving" ? t("saving") : state === "saved" ? t("saved") : t("save")}
          </button>
        ) : (
          <button type="button" className="button button-primary" onClick={() => setLang(language)}>
            {t("save")}
          </button>
        )}
      </form>

      {isFull && (
        <>
          <SectionTitle>{t("replyChannel")}</SectionTitle>
          <Card className="form">
            <p className="small">{t("replyChannelBody")}</p>
            <Field label={t("replyChannel")} hint={viaState === "saved" ? t("saved") : viaState === "saving" ? t("saving") : undefined}>
              <select value={replyVia} disabled={viaState === "saving"} onChange={(e) => void saveReplyVia(e.target.value as ReplyVia)}>
                <option value="area">{t("replyHere")}</option>
                <option value="email">{t("email")}</option>
                <option value="whatsapp">{t("whatsapp")}</option>
              </select>
            </Field>
            {viaState === "error" && <ErrorNote>{t("somethingWrong")}</ErrorNote>}
          </Card>

          <SectionTitle>{t("tripNotices")}</SectionTitle>
          <NoticesCard me={me} />

          <SectionTitle>{t("privacy")}</SectionTitle>
          <Card className="privacy-card">
            <p className="small">{t("privacyBody")}</p>
            <a href="/privacy" className="doc-link">
              <IconShieldCheck size={16} /> {t("readPrivacy")}
            </a>
            <p className="notices-subtitle">{t("serviceAgreement")}</p>
            <div className="chips">
              {brands.filter((b) => hasServiceConsent(me, b.code)).map((b) => (
                <span key={b.code} className="chip chip-confirmed">
                  {t("consentAccepted", { brand: b.name })}
                </span>
              ))}
              {!brands.some((b) => hasServiceConsent(me, b.code)) && <span className="chip">{t("consentPending")}</span>}
            </div>
            <p className="notices-subtitle">{t("consents")}</p>
            <p className="small muted">{t("consentsBody")}</p>
            <ul className="list">
              {brands.map((b) => (
                <li key={b.code} className="list-row">
                  <label className="checkbox">
                    <input type="checkbox" checked={consentOf(b.code)} disabled={consentBusy === b.code} onChange={(e) => void toggleConsent(b.code, e.target.checked)} />
                    <span>{t("marketingFrom", { brand: b.name })}</span>
                  </label>
                </li>
              ))}
            </ul>
            <p className="notices-subtitle">{t("yourData")}</p>
            <div className="row-buttons">
              <button type="button" className="button button-small" onClick={() => void exportData()} disabled={exportState === "busy"}>
                <IconDownload size={16} /> {exportState === "busy" ? t("exporting") : exportState === "done" ? t("exported") : t("exportData")}
              </button>
              {deleteState !== "sent" && (
                <button type="button" className="button button-small" onClick={() => setDeleteState("confirm")} disabled={deleteState === "confirm" || deleteState === "busy"}>
                  <IconTrash size={16} /> {t("deleteAccount")}
                </button>
              )}
            </div>
            {exportState === "error" && <ErrorNote onRetry={() => void exportData()}>{t("somethingWrong")}</ErrorNote>}
            {(deleteState === "confirm" || deleteState === "busy" || deleteState === "error") && (
              <div className="delete-confirm" role="group" aria-label={t("deleteAccount")}>
                <p className="small">{t("deleteAccountExplain")}</p>
                {deleteState === "error" && <ErrorNote>{t("somethingWrong")}</ErrorNote>}
                <div className="row-buttons">
                  <button type="button" className="button button-small button-danger" onClick={() => void requestDeletion()} disabled={deleteState === "busy"}>
                    <IconTrash size={16} /> {deleteState === "busy" ? t("saving") : t("deleteAccountConfirm")}
                  </button>
                  <button type="button" className="button button-small" onClick={() => setDeleteState("idle")} disabled={deleteState === "busy"}>
                    {t("cancel")}
                  </button>
                </div>
              </div>
            )}
            {deleteState === "sent" && (
              <p className="notice">
                <IconMailCheck size={18} /> <span className="notice-text small">{t("deleteRequested")}</span>
              </p>
            )}
            <p className="muted small">{t("dataRequestsNote")}</p>
          </Card>

          <SectionTitle>{t("messagesSent")}</SectionTitle>
          <Card>
            {messages === undefined ? (
              <Spinner />
            ) : messages.length === 0 ? (
              <p className="muted small">{t("messagesEmpty")}</p>
            ) : (
              <ul className="list">
                {messages.map((m) => (
                  <li key={m.id} className="list-row message-row">
                    <span className="list-text">
                      <strong>{m.subject ?? m.kind}</strong>
                      <span className="muted small">
                        {m.channel} · {formatDateTime(m.createdAt, lang)}
                      </span>
                      <span className="small message-body">{m.body}</span>
                      {m.link && (
                        <a href={m.link} className="doc-link" rel="external">
                          {t("openLink")} <IconExternalLink size={14} />
                        </a>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {messages && messages.some((m) => m.status === "simulated") && <p className="muted small">{t("messagesStagingNote")}</p>}
          </Card>
        </>
      )}

      <button type="button" className="button button-block" onClick={() => void logout()}>
        <IconLogout size={18} /> {t("signOut")}
      </button>

      <SectionTitle>{t("legal")}</SectionTitle>
      <p className="muted small">{brand.groupStatement[lang] ?? brand.groupStatement.en}</p>
      <p className="muted small">{t("operatedBy", { entity: brand.legalEntity })}</p>
    </main>
  );
}
